<template>
  <div class="state-buttons">
    <div v-if="item.sequence_id">
      Inherited from <router-link :to="sequenceLink">{{ item.sequence_name }}</router-link>
    </div>
    <div v-else class="promo-details-status-div">
      <RfPromoPublishStatus :item="item" />
      <div class="status-all-buttons">
        <RfButton
          v-if="promoStatusLabel === 'Scheduled' || promoStatusLabel === 'Pending'"
          size="small"
          variant="outlined"
          color="success"
          icon="md:play_arrow"
          buttonText="Start"
          :tooltip="{ location: 'bottom' }"
          :disabled="userStore.isDisabledWithMessage"
          @click="callPublishDialog('start')"
        />
        <RfButton
          v-if="promoStatusLabel === 'Paused'"
          size="small"
          variant="outlined"
          color="success"
          icon="md:play_arrow"
          buttonText="Resume"
          :tooltip="{ location: 'bottom' }"
          :disabled="userStore.isDisabledWithMessage"
          @click="callPublishDialog('resume')"
        />
        <RfButton
          v-if="!item.is_enabled && dayjs(item.start_date).isAfter(dayjs())"
          icon="mdi:mdi-clock-fast"
          buttonText="Schedule"
          size="small"
          variant="outlined"
          :tooltip="{ location: 'bottom' }"
          color="info"
          :disabled="userStore.isDisabledWithMessage"
          @click="callPublishDialog('ready')"
        />
        <RfButton
          v-if="promoStatusLabel === 'Running'"
          size="small"
          variant="outlined"
          color="info"
          icon="md:pause"
          buttonText="Pause"
          :tooltip="{ location: 'bottom' }"
          :disabled="userStore.isDisabledWithMessage"
          @click="callPublishDialog('pause')"
        />
        <RfButton
          v-if="canEnd"
          variant="outlined"
          size="small"
          color="info"
          icon="md:stop"
          buttonText="End"
          :tooltip="{ location: 'bottom' }"
          :disabled="userStore.isDisabledWithMessage"
          @click="callPublishDialog('end')"
        />
      </div>
    </div>
    <v-dialog v-model="promoPublishDialog" width="580">
      <v-card flat>
        <v-card-title class="headline">
          {{
            `${capitalize(StatusesSimplifiedConstants[promoPublishDialogContent]?.action)} ${capitalize(itemType)}`
          }}
        </v-card-title>
        <v-card-text>
          <div>
            Are you sure you want to
            {{
              (
                StatusesSimplifiedConstants[promoPublishDialogContent]?.action ||
                promoPublishDialogContent
              )?.toLowerCase()
            }}
            this {{ itemType }}?
            {{ publishHelpText }}
          </div>
          <RfStartStopDatePicker
            v-if="promoPublishDialogContent === StatusesSimplifiedConstants.ready.type"
            ref="dateRangePicker"
            class="mt-4"
            :dateRange="{
              startDate: dayjs(item.start_date).utc().toISOString(),
              endDate: dayjs(item.end_date).utc().toISOString(),
            }"
            :model="item"
          />
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            size="large"
            class="cancel-btn"
            variant="outlined"
            width="100px"
            @click.stop="promoPublishDialog = false"
            >Close</v-btn
          >
          <v-btn
            size="large"
            variant="flat"
            color="accent"
            width="150px"
            class="submit-publish-button"
            @click="submitPublish"
            >{{
              capitalize(
                StatusesSimplifiedConstants[promoPublishDialogContent]?.action ||
                  promoPublishDialogContent,
              )
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RfPromoPublishStatus from "@/components/RfPromotions/RfPromoPublishStatus.vue";
import PromoStatusMixin from "@/utils/PromoStatusMixin";
import RfButton from "@/components/buttons/RfButton.vue";
import StringUtils from "@/utils/StringUtils";
import { useToastsStore } from "@/pinia/toastsStore";
import dayjs from "dayjs";
import { StatusesSimplifiedConstants } from "@/utils/constants/PromptStatusesConstants";
import { isPushNotification } from "@/utils/prompts/promptHelpers";
import { ref } from "vue";
import { useSequencesStore } from "@/pinia/sequencesStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useUserStore } from "@/pinia/userStore";
import RfStartStopDatePicker from "../RfStartStopDatePicker.vue";

export default {
  name: "RfStateButtonsInterior",
  components: { RfPromoPublishStatus, RfButton, RfStartStopDatePicker },
  mixins: [PromoStatusMixin],
  props: ["item"],
  setup: () => ({
    capitalize: StringUtils.capitalize,
    toastsStore: useToastsStore(),
    dayjs,
    StatusesSimplifiedConstants,
    isPushNotification,
    dateRangePicker: ref(),
    sequencesStore: useSequencesStore(),
    promptStore: usePromptStore(),
    userStore: useUserStore(),
  }),
  data: () => ({ promoPublishDialog: false, promoPublishDialogContent: null }),
  computed: {
    canEnd() {
      return (
        this.promoStatusLabel === "Running" ||
        this.promoStatusLabel === "Paused" ||
        this.promoStatusLabel === "Limited"
      );
    },
    itemType() {
      return this.item.subject_name === "Sequence" ? "guide" : "prompt";
    },
    promoStatusLabel() {
      return this.promoStatus(this.item).label;
    },
    publishHelpText() {
      let message;
      let subject = this.itemType;
      if (subject === "guide") {
        subject += " and its items";
      }
      switch (this.promoPublishDialogContent) {
        case "start":
        case "resume":
          message = `The ${subject} will run immediately.`;
          break;
        case "ready":
          message = `The ${subject} will run on the scheduled start date.`;
          break;
        case "end":
        case "pause":
          message = `The ${subject} will stop running immediately.`;
          break;
        default:
          message = "";
          break;
      }
      return message;
    },
    sequenceLink() {
      return { path: `/apps/${this.$route.params.aid}/experiences/${this.item.sequence_id}` };
    },
  },
  methods: {
    callPublishDialog(action) {
      this.promoPublishDialog = true;
      this.promoPublishDialogContent = action;
    },
    getToday() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${now.getFullYear()}-${month}-${day}`;
    },
    getYesterday() {
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);

      const month = String(yesterday.getMonth() + 1).padStart(2, "0");
      const day = String(yesterday.getDate()).padStart(2, "0");
      return `${yesterday.getFullYear()}-${month}-${day}`;
    },
    async submitPublish() {
      try {
        let isStarting = false;
        const copyItem = { ...this.item };
        switch (this.promoPublishDialogContent) {
          case "start":
            copyItem.is_enabled = true;
            copyItem.start_date = this.getToday();
            isStarting = true;
            break;
          case "end":
            copyItem.is_enabled = false;
            copyItem.end_date = this.getYesterday(); // yesterday
            break;
          case "resume":
          case "ready":
            copyItem.is_enabled = true;
            isStarting = true;
            break;
          case "pause":
            copyItem.is_enabled = false;
            break;
          default:
            break;
        }
        this.promoPublishDialog = false;

        if (this.promoPublishDialogContent === StatusesSimplifiedConstants.ready.type) {
          if (this.dateRangePicker) {
            const { fullStartDate, fullEndDate } = this.dateRangePicker;
            if (dayjs(fullStartDate) >= dayjs(fullEndDate))
              throw new Error("Start date must be before start date");

            copyItem.start_date = fullStartDate;
            copyItem.end_date = fullEndDate;
          }
        }

        if (this.itemType === "guide") {
          if (isStarting && !copyItem.paths.length) {
            this.toastsStore.create({
              type: "error",
              body: "Cannot start or resume a guide with no items",
            });
          } else {
            await this.sequencesStore.updateSequence({
              appId: this.item.app_id,
              sequenceId: this.item.id,
              modelSequence: copyItem,
            });
            this.toastsStore.create({ type: "success", body: "Guide updated successfully" });
          }
        } else {
          await this.promptStore.updatePromptState({
            appId: this.item.app_id,
            pathId: this.item.id,
            stateData: {
              is_enabled: copyItem.is_enabled,
              start_date: copyItem.start_date,
              end_date: copyItem.end_date,
            },
          });
          this.toastsStore.create({ type: "success", body: "Prompt updated successfully" });
        }
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message || "Failed to update status" });
      }
    },
  },
};
</script>
